import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Heart, Loader2, CheckCircle2, AlertCircle, Calendar, Banknote } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DonorPayInstallmentPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const requestedYear = parseInt(searchParams.get('year') || `${new Date().getFullYear()}`, 10);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [donor, setDonor] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    year: requestedYear,
    amount: '',
    paymentDate: new Date().toISOString().slice(0, 10),
    paymentMethod: 'UPI',
    transactionId: '',
    receiptUrl: '',
    notes: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/scholarship/donor-pay/${token}`);
        setDonor(data);
        setForm(f => ({
          ...f,
          amount: f.amount || data.defaultAmount || '',
        }));
      } catch (err) {
        setError(err.response?.data?.detail || 'This payment link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseInt(form.amount, 10) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!form.transactionId.trim()) {
      toast.error('Please enter the transaction / reference ID');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/scholarship/donor-pay/${token}`, {
        year: parseInt(form.year, 10),
        amount: parseInt(form.amount, 10),
        paymentDate: form.paymentDate,
        paymentMethod: form.paymentMethod,
        transactionId: form.transactionId,
        receiptUrl: form.receiptUrl || null,
        notes: form.notes || null,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-orange-50">
        <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-orange-50 p-4">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900">Payment link not valid</h2>
          <p className="text-sm text-gray-600 mt-2">{error}</p>
          <Link to="/scholarship/donor-registration" className="inline-block mt-4 text-sm text-orange-600 hover:underline">
            Go to the public donor form →
          </Link>
        </Card>
      </div>
    );
  }

  const alreadyPaid = donor?.paidYears?.includes(parseInt(form.year, 10));

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <Heart className="w-10 h-10 text-orange-600 mx-auto mb-2" />
          <h1 className="text-2xl font-bold text-gray-900">Shiksha Mitra — Installment Payment</h1>
          <p className="text-gray-500 text-sm mt-1">शिक्षा मित्र किस्त भुगतान</p>
        </div>

        {submitted ? (
          <Card className="p-8 text-center" data-testid="donor-pay-success">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-gray-900">Thank you, {donor.fullName}!</h2>
            <p className="text-sm text-gray-600 mt-2">
              Your {form.year} contribution has been submitted and is pending verification.
              Our team will reconcile it with your bank/UPI reference and mark it as paid shortly.
              You'll get a confirmation email once verified.
            </p>
            <p className="text-xs text-gray-400 mt-3">आपका योगदान सत्यापन हेतु प्राप्त हुआ है। शीघ्र ही पुष्टि का ईमेल भेजा जाएगा।</p>
          </Card>
        ) : (
          <Card className="p-6 md:p-8" data-testid="donor-pay-card">
            <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mb-5">
              <p className="text-sm text-orange-900">
                <b>Account:</b> {donor.fullName}{donor.fatherOrHusbandName ? <> ({donor.fatherOrHusbandName})</> : null}
                {donor.membershipNo ? <> · <span className="font-mono text-xs">{donor.membershipNo}</span></> : null}
                {donor.city ? <> · {donor.city}</> : null}
              </p>
              <p className="text-xs text-orange-700 mt-1">{donor.membershipType}</p>
              {donor.paidYears?.length > 0 && (
                <p className="text-xs text-green-700 mt-2">
                  ✓ Previously paid: {donor.paidYears.join(', ')}
                </p>
              )}
            </div>

            {alreadyPaid && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md p-3 mb-4">
                Heads up — {form.year} is already marked paid for this account. If you've made an additional payment, please choose a different year or contact the Mahasabha team.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Installment Year</Label>
                  <select
                    name="year"
                    value={form.year}
                    onChange={handleChange}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 text-sm bg-white"
                    data-testid="donor-pay-year"
                  >
                    {[2024, 2025, 2026, 2027, 2028, 2029, 2030, 2031].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Amount (₹)</Label>
                  <div className="relative mt-1">
                    <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number" min="1" required
                      name="amount" value={form.amount} onChange={handleChange}
                      className="pl-10"
                      data-testid="donor-pay-amount"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Payment Date</Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="date" required
                      name="paymentDate" value={form.paymentDate} onChange={handleChange}
                      className="pl-10"
                      data-testid="donor-pay-date"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Payment Method</Label>
                  <select
                    name="paymentMethod"
                    value={form.paymentMethod}
                    onChange={handleChange}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 text-sm bg-white"
                    data-testid="donor-pay-method"
                  >
                    {['UPI', 'Bank Transfer', 'Cheque', 'Cash', 'Other'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Transaction / Reference ID</Label>
                <Input
                  required
                  name="transactionId" value={form.transactionId} onChange={handleChange}
                  placeholder="UPI ref / UTR / Cheque number"
                  className="mt-1"
                  data-testid="donor-pay-txn-id"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Receipt URL (optional)</Label>
                <Input
                  type="url"
                  name="receiptUrl" value={form.receiptUrl} onChange={handleChange}
                  placeholder="https://drive.google.com/..."
                  className="mt-1"
                  data-testid="donor-pay-receipt"
                />
                <p className="text-xs text-gray-400 mt-1">If your bank/UPI app provides a receipt, share a link here (Google Drive, Dropbox, etc.).</p>
              </div>

              <div>
                <Label className="text-sm font-medium">Notes (optional)</Label>
                <textarea
                  rows={2}
                  name="notes" value={form.notes} onChange={handleChange}
                  placeholder="Anything we should know?"
                  className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 text-sm bg-white"
                  data-testid="donor-pay-notes"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-orange-600 hover:bg-orange-700 mt-2"
                data-testid="donor-pay-submit"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting…</> : `Submit ${form.year} Payment`}
              </Button>
              <p className="text-xs text-center text-gray-400">
                After submission, the Mahasabha team will verify and mark this installment as paid in our records.
              </p>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DonorPayInstallmentPage;
